(function () {
  'use strict';
  const instances = new WeakMap();
  const rootWindow = typeof globalThis !== 'undefined' && globalThis.window
    ? globalThis.window
    : typeof window !== 'undefined'
      ? window
      : null;
  const rootDocument = (rootWindow && rootWindow.document) || (typeof document !== 'undefined' ? document : null);
  const DEBUG = (() => {
    try {
      const loc = (rootWindow && rootWindow.location) || (typeof location !== 'undefined' ? location : null);
      return !!(loc && /(?:^|[?&])armore_variant_debug=1(?:&|$)/.test(loc.search || ''));
    } catch (_) {
      return false;
    }
  })();
  const log = (...args) => {
    if (DEBUG && typeof console !== 'undefined') console.info('[ArmoreProductOptions]', ...args);
  };

  /**
   * AbortController ≠ addEventListener({signal}) support.
   * - AC: Safari 11.1+ / iOS 11.3+
   * - Listener signal: Safari 15+ / modern Chromium
   * Engines with AC but without listener signals must use manual removeEventListener tracking.
   */
  function supportsListenerSignal() {
    if (typeof AbortController !== 'function') return false;
    try {
      const ac = new AbortController();
      const el = rootDocument && typeof rootDocument.createElement === 'function'
        ? rootDocument.createElement('div')
        : null;
      if (!el || typeof el.addEventListener !== 'function') return false;
      const noop = () => {};
      el.addEventListener('armore-signal-probe', noop, {signal: ac.signal});
      ac.abort();
      return true;
    } catch (_) {
      return false;
    }
  }

  function createManualListenerHub() {
    const entries = [];
    return {
      mode: 'manual',
      listen(target, type, handler, options = {}) {
        if (!target || typeof target.addEventListener !== 'function') return;
        const capture = !!options.capture;
        target.addEventListener(type, handler, capture);
        entries.push([target, type, handler, capture]);
      },
      abort() {
        while (entries.length) {
          const [target, type, handler, capture] = entries.pop();
          try { target.removeEventListener(type, handler, capture); } catch (_) {}
        }
      }
    };
  }

  function createListenerHub() {
    if (supportsListenerSignal()) {
      const abort = new AbortController();
      const orphan = [];
      return {
        mode: 'signal',
        listen(target, type, handler, options = {}) {
          if (!target || typeof target.addEventListener !== 'function') return;
          try {
            target.addEventListener(type, handler, {...options, signal: abort.signal});
          } catch (_) {
            // Probe passed but this call rejected signal — track manually.
            const capture = !!options.capture;
            target.addEventListener(type, handler, capture);
            orphan.push([target, type, handler, capture]);
          }
        },
        abort() {
          try { abort.abort(); } catch (_) {}
          while (orphan.length) {
            const [target, type, handler, capture] = orphan.pop();
            try { target.removeEventListener(type, handler, capture); } catch (_) {}
          }
        }
      };
    }
    return createManualListenerHub();
  }

  function eventElement(event) {
    const t = event && event.target;
    if (!t) return null;
    if (typeof Element !== 'undefined' && t instanceof Element) return t;
    if (t.nodeType === 3 && t.parentElement) return t.parentElement;
    return typeof t.closest === 'function' ? t : null;
  }

  function failClosedIds(form) {
    form.querySelectorAll('input[name="id"], select[name="id"]').forEach(input => {
      input.value = '';
      input.disabled = true;
    });
  }

  function init(form) {
    if (!form || !form.matches('[data-armore-product-form]') || instances.has(form)) return;
    // Deluxe Ring uses ArmoreDeluxeRing as sole options/purchase-state owner (avoid dual listeners).
    if (form.getAttribute('data-armore-deluxe-ring') === 'true' || form.getAttribute('data-armore-purchase-authority') === 'deluxe-ring') {
      log('init skipped — ArmoreDeluxeRing owns this form');
      if (rootWindow && rootWindow.ArmoreDeluxeRing && typeof rootWindow.ArmoreDeluxeRing.bindForm === 'function') {
        rootWindow.ArmoreDeluxeRing.bindForm(form);
      }
      return;
    }

    const hub = createListenerHub();
    const listen = hub.listen;
    const variantInput = form.querySelector('[data-armore-variant-id]');
    const advanced = form.querySelector('[data-advsz]');
    const inputs = () => Array.from(form.querySelectorAll('[data-single-option-selector]'));
    const error = form.querySelector('[data-armore-size-error]');
    let closeTimer;
    let frame;
    let lastFocus;
    let previousOverflow;
    let opened = false;
    let guideHydrated = false;
    let guideView = 'list';
    let activeCategory = 'ring';
    let restoreNativeSubmit = null;
    let restore = () => {};
    let close = () => {};

    function syncSwatches() {
      form.querySelectorAll('.swatch-view-item').forEach(item => {
        const radio = item.querySelector('input[data-single-option-selector]');
        if (!radio) return;
        item.setAttribute('aria-checked', String(radio.checked));
        item.tabIndex = radio.checked ? 0 : -1;
        item.querySelector('.swatch-selector')?.classList.toggle('swatch-selected', radio.checked);
      });
    }

    function selectInput(input, value) {
      if (!input) return false;
      if (input.type === 'radio') input.checked = true;
      else input.value = value;
      input.dispatchEvent(new Event('input', {bubbles: true}));
      input.dispatchEvent(new Event('change', {bubbles: true}));
      return true;
    }

    function chooseSwatch(item) {
      if (!item || item.closest('form') !== form) return;
      const radio = item.querySelector('input[data-single-option-selector]');
      if (!radio || radio.disabled || item.getAttribute('aria-disabled') === 'true') return;
      if (!radio.checked) selectInput(radio, radio.value);
      syncSwatches();
      log('chooseSwatch', radio.value, 'variantId', form.querySelector('[name="id"]')?.value, 'disabled', form.querySelector('[name="id"]')?.disabled);
    }

    function destroyInstance() {
      try { close(true, false); } catch (_) {}
      clearTimeout(closeTimer);
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
      if (typeof restoreNativeSubmit === 'function') {
        try { restoreNativeSubmit(); } catch (_) {}
      }
      hub.abort();
      instances.delete(form);
    }

    try {
      // --- swatches (always) ---
      listen(form, 'click', event => {
        const el = eventElement(event);
        if (!el) return;
        const item = el.closest('.swatch-view-item');
        if (item && el.tagName !== 'INPUT') chooseSwatch(item);
      });
      listen(form, 'keydown', event => {
        const el = eventElement(event);
        if (!el) return;
        const item = el.closest('.swatch-view-item');
        if (!item || item.closest('form') !== form) return;
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          chooseSwatch(item);
        } else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
          const items = Array.from(item.parentElement.children).filter(el => el.matches('.swatch-view-item') && el.getAttribute('aria-disabled') !== 'true');
          const index = items.indexOf(item);
          const step = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
          const next = event.key === 'Home' ? items[0] : event.key === 'End' ? items[items.length - 1] : items[(index + step + items.length) % items.length];
          if (next) { event.preventDefault(); chooseSwatch(next); next.focus(); }
        }
      });
      listen(form, 'change', syncSwatches);
      syncSwatches();
      log('init', form.id || form.getAttribute('id') || '(form)', 'advsz', !!advanced);

      if (advanced) {
        const index = 'option' + (Number(advanced.dataset.optIndex) + 1);
        const sizeInputs = () => inputs().filter(input => input.dataset.index === index);
        const selectedValue = () => sizeInputs().find(input => input.type !== 'radio' || input.checked)?.value;
        const overlay = advanced.querySelector('.advsz__overlay');
        const modal = advanced.querySelector('.advsz__modal');
        const trigger = advanced.querySelector('.advsz__trigger');
        const triggerText = advanced.querySelector('.advsz__trigger-text');
        const back = advanced.querySelector('.advsz__back');
        const list = advanced.querySelector('[data-panel="list"]');
        const menu = advanced.querySelector('[data-panel="guide-menu"]');
        const guide = advanced.querySelector('[data-panel="guide"]');
        const slot = advanced.querySelector('[data-guide-slot]');
        const title = advanced.querySelector('.advsz__title');
        const buttons = Array.from(advanced.querySelectorAll('.advsz__option'));
        const payment = form.querySelector('[data-armore-payment]');
        const nativeWrapper = sizeInputs()[0]?.closest('.selector-wrapper');

        // Mandatory for a consistent Adv Size + ATC gate (present in Live product-form.liquid).
        const missing = [];
        if (!trigger) missing.push('.advsz__trigger');
        if (!triggerText) missing.push('.advsz__trigger-text');
        if (!overlay) missing.push('.advsz__overlay');
        if (!modal) missing.push('.advsz__modal');
        if (!back) missing.push('.advsz__back');
        if (!title) missing.push('.advsz__title');
        if (missing.length) {
          failClosedIds(form);
          const msg = (advanced.dataset.requiredMessage || 'Select a size') + ' (size UI unavailable)';
          if (error) { error.textContent = msg; error.hidden = false; }
          else showError(form, msg);
          listen(form, 'submit', event => {
            event.preventDefault();
            event.stopImmediatePropagation();
          }, {capture: true});
          listen(form, 'click', event => {
            const el = eventElement(event);
            if (el && el.closest('[data-add-to-cart], .shopify-payment-button, [data-armore-payment]')) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          }, {capture: true});
          log('advsz missing mandatory nodes', missing.join(', '));
          restore = syncSwatches;
        } else {
          if (nativeWrapper) nativeWrapper.hidden = true;

          function syncSize() {
            const value = selectedValue();
            const chosen = advanced.dataset.selected === 'true' && buttons.some(button => button.dataset.optionValue === value);
            advanced.dataset.selected = String(chosen);
            buttons.forEach(button => {
              const selected = chosen && button.dataset.optionValue === value;
              button.classList.toggle('is-selected', selected);
              button.setAttribute('aria-pressed', String(selected));
            });
            triggerText.textContent = chosen ? advanced.dataset.sizeLabel + ' ' + value : advanced.dataset.unselectedLabel;
            if (payment) payment.hidden = !chosen;
            const idInputs = form.querySelectorAll('input[name="id"], select[name="id"]');
            if (!chosen) {
              idInputs.forEach(input => {
                input.value = '';
                input.disabled = true;
              });
            } else {
              idInputs.forEach(input => {
                input.disabled = false;
              });
            }
            if (chosen && error) { error.hidden = true; error.textContent = ''; }
            trigger.setAttribute('aria-invalid', String(!chosen && !!error && !error.hidden));
          }

          function hideAllPanels() {
            if (list) list.hidden = true;
            if (menu) menu.hidden = true;
            if (guide) guide.hidden = true;
          }

          function showList() {
            hideAllPanels();
            if (list) list.hidden = false;
            back.classList.add('advsz__back--hidden');
            title.textContent = advanced.dataset.chooseTitle;
            guideView = 'list';
            modal.setAttribute('data-view', 'list');
          }

          function showMenu() {
            hideAllPanels();
            if (menu) menu.hidden = false;
            else if (guide) guide.hidden = false;
            back.classList.remove('advsz__back--hidden');
            title.textContent = 'SIZE GUIDE';
            guideView = 'menu';
            modal.setAttribute('data-view', 'menu');
          }

          function hydrateRingGuide() {
            if (guideHydrated || !slot) return;
            const preferred = advanced.dataset.guideSection;
            const source = (preferred && rootDocument.getElementById('shopify-section-' + preferred)) || form.closest('[data-section-id]')?.querySelector('[data-advsg="root"]') || rootDocument.querySelector('[data-advsg="root"]');
            const content = source?.querySelector('[data-advsg-wrap]');
            if (content) { slot.innerHTML = content.innerHTML; guideHydrated = true; }
          }

          function normalizeCategory(category) {
            if (!category) return 'rings';
            return category === 'ring' ? 'rings' : category;
          }

          function setCategory(category) {
            const cat = normalizeCategory(category);
            activeCategory = cat;
            if (!guide) return;
            guide.querySelectorAll('[data-sg-tab]').forEach(tab => {
              const tabCat = normalizeCategory(tab.getAttribute('data-sg-tab'));
              const selected = tabCat === cat;
              tab.setAttribute('aria-selected', String(selected));
              tab.setAttribute('tabindex', selected ? '0' : '-1');
            });
            guide.querySelectorAll('[data-sg-panel]').forEach(panel => {
              const panelCat = normalizeCategory(panel.getAttribute('data-sg-panel'));
              panel.hidden = panelCat !== cat;
            });
          }

          function showDetail(category) {
            hideAllPanels();
            if (guide) guide.hidden = false;
            back.classList.remove('advsz__back--hidden');
            title.textContent = 'SIZE GUIDE';
            guideView = 'detail';
            modal.setAttribute('data-view', 'detail');
            hydrateRingGuide();
            setCategory(category || activeCategory || 'rings');
          }

          function finishClose(restoreFocus) {
            clearTimeout(closeTimer);
            if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
            overlay.classList.remove('is-open');
            overlay.hidden = true;
            modal.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
            if (opened) rootDocument.documentElement.style.overflow = previousOverflow;
            opened = false;
            if (restoreFocus && lastFocus?.isConnected) lastFocus.focus();
          }

          close = (immediate = false, restoreFocus = true) => {
            if (!opened) return;
            overlay.classList.remove('is-open');
            clearTimeout(closeTimer);
            const reduce = rootWindow && rootWindow.matchMedia && rootWindow.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (immediate || reduce) finishClose(restoreFocus);
            else closeTimer = setTimeout(() => finishClose(restoreFocus), 1000);
          };

          function open() {
            clearTimeout(closeTimer);
            if (!opened) {
              lastFocus = rootDocument.activeElement;
              previousOverflow = rootDocument.documentElement.style.overflow;
            }
            opened = true;
            showList();
            overlay.hidden = false;
            modal.hidden = false;
            trigger.setAttribute('aria-expanded', 'true');
            rootDocument.documentElement.style.overflow = 'hidden';
            frame = requestAnimationFrame(() => { overlay.classList.add('is-open'); modal.focus(); });
          }

          function requireSize(event) {
            if (advanced.dataset.selected === 'true') return;
            event.preventDefault();
            event.stopImmediatePropagation();
            failClosedIds(form);
            if (error) { error.textContent = advanced.dataset.requiredMessage; error.hidden = false; }
            trigger.setAttribute('aria-invalid', 'true');
            open();
          }

          listen(form, 'submit', requireSize, {capture: true});
          listen(form, 'click', event => {
            const el = eventElement(event);
            if (el && el.closest('[data-add-to-cart], .shopify-payment-button, [data-armore-payment]')) requireSize(event);
          }, {capture: true});

          try {
            const origSubmit = form.submit;
            form.submit = function() {
              if (advanced.dataset.selected !== 'true') {
                failClosedIds(form);
                open();
                return false;
              }
              return origSubmit.apply(form, arguments);
            };
            restoreNativeSubmit = () => {
              try { form.submit = origSubmit; } catch (err) {}
            };
          } catch (err) {}

          listen(trigger, 'click', open);
          listen(overlay, 'click', event => { if (event.target === overlay) close(); });
          listen(advanced.querySelector('.advsz__close'), 'click', () => close());
          listen(back, 'click', () => {
            if (guideView === 'detail') {
              showMenu();
              advanced.querySelector('[data-sg-open], [data-sg-open-category]')?.focus();
            } else {
              showList();
              advanced.querySelector('[data-open-guide]')?.focus();
            }
          });
          listen(modal, 'keydown', event => {
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(modal.querySelectorAll('button:not([disabled]), a[href], input, select, textarea, [tabindex="0"]')).filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[hidden]'));
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!first) { event.preventDefault(); modal.focus(); }
            else if (event.shiftKey && (rootDocument.activeElement === first || rootDocument.activeElement === modal)) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && (rootDocument.activeElement === last || rootDocument.activeElement === modal)) { event.preventDefault(); first.focus(); }
          });

          const guideButton = advanced.querySelector('[data-open-guide]');
          if (guideButton) listen(guideButton, 'click', () => {
            if (menu) {
              showMenu();
              menu.querySelector('[data-sg-open], [data-sg-open-category]')?.focus();
            } else {
              showDetail('rings');
              back.focus();
            }
          });

          if (menu) {
            menu.querySelectorAll('[data-sg-open], [data-sg-open-category]').forEach(row => {
              listen(row, 'click', () => {
                const cat = row.getAttribute('data-sg-open') || row.getAttribute('data-sg-open-category') || 'rings';
                showDetail(cat);
                guide?.querySelector('[data-sg-tab][aria-selected="true"]')?.focus();
              });
            });
          }
          if (guide) {
            guide.querySelectorAll('[data-sg-tab]').forEach(tab => {
              listen(tab, 'click', () => setCategory(tab.getAttribute('data-sg-tab') || 'rings'));
            });
            guide.querySelectorAll('details').forEach(details => {
              listen(details, 'toggle', () => {
                const summary = details.querySelector('summary, .hdt-sg__acc-summary');
                if (summary) summary.setAttribute('aria-expanded', String(details.open));
              });
            });
            guide.querySelectorAll('[data-sg-unit]').forEach(btn => {
              listen(btn, 'click', () => {
                const unit = btn.getAttribute('data-sg-unit');
                if (!unit) return;
                guide.setAttribute('unit', unit);
                guide.querySelectorAll('[data-sg-unit]').forEach(b => {
                  b.setAttribute('aria-pressed', String(b.getAttribute('data-sg-unit') === unit));
                });
              });
            });
          }

          buttons.forEach(button => listen(button, 'click', () => {
            const value = button.dataset.optionValue;
            const input = sizeInputs().find(input => input.type !== 'radio' || input.value === value);
            if (!input) return;
            advanced.dataset.selected = 'true';
            selectInput(input, value);
            form.querySelectorAll('input[name="id"], select[name="id"]').forEach(i => { i.disabled = false; });
            syncSize();
            close();
          }));
          listen(form, 'change', event => {
            if (sizeInputs().includes(event.target) && event.isTrusted) advanced.dataset.selected = 'true';
            syncSize();
          });
          restore = () => { close(true, false); syncSwatches(); syncSize(); };
          syncSize();
        }
      } else if (variantInput) {
        variantInput.disabled = false;
        restore = syncSwatches;
      } else {
        restore = syncSwatches;
      }

      // Commit only after successful wiring
      const instance = {destroy: destroyInstance, restore};
      instances.set(form, instance);
      if (rootWindow) listen(rootWindow, 'pageshow', event => { if (event.persisted && form.isConnected) restore(); });
    } catch (err) {
      log('init failed — cleaning partial listeners', err && err.message ? err.message : err);
      try { failClosedIds(form); } catch (_) {}
      destroyInstance();
      if (typeof console !== 'undefined') console.error('[ArmoreProductOptions] init failed', err);
    }
  }

  const forms = root => (root || rootDocument).querySelectorAll('[data-armore-product-form]');
  function boot(root) {
    if (!(root || rootDocument)) return;
    forms(root || rootDocument).forEach(init);
  }
  function showError(root, message) {
    let error = root.querySelector('[data-armore-cart-error]');
    if (!error) {
      error = rootDocument.createElement('p');
      error.className = 'errors';
      error.setAttribute('data-armore-cart-error', '');
      error.setAttribute('role', 'status');
      error.setAttribute('aria-live', 'polite');
      root.appendChild(error);
    }
    error.textContent = message;
  }

  const exportTarget = rootWindow || (typeof globalThis !== 'undefined' ? globalThis : null);
  if (exportTarget) {
    exportTarget.ArmoreProductOptions = {
      init,
      showError,
      destroy(form) {
        instances.get(form)?.destroy();
        // Deluxe Ring forms never enter `instances`; still clear their controller.
        if (rootWindow && rootWindow.ArmoreDeluxeRing && typeof rootWindow.ArmoreDeluxeRing.destroy === 'function') {
          rootWindow.ArmoreDeluxeRing.destroy(form);
        }
      },
      boot,
    };
  }

  /**
   * Boot scheduling:
   * - readyState loading → wait for DOMContentLoaded
   * - interactive or complete → boot immediately (covers script injected after window.load)
   * - load listener only if not yet complete (second pass for late-deferred deps)
   * ProductForm.constructor also calls init(form) for forms created by Broadcast.
   */
  if (rootDocument) {
    if (rootDocument.readyState === 'loading') {
      rootDocument.addEventListener('DOMContentLoaded', () => boot(rootDocument), {once: true});
    } else {
      boot(rootDocument);
    }
    rootDocument.addEventListener('shopify:section:load', event => boot(event.target));
    rootDocument.addEventListener('shopify:section:unload', event => {
      forms(event.target).forEach(form => {
        instances.get(form)?.destroy();
        if (rootWindow && rootWindow.ArmoreDeluxeRing && typeof rootWindow.ArmoreDeluxeRing.destroy === 'function') {
          rootWindow.ArmoreDeluxeRing.destroy(form);
        }
      });
      if (rootWindow && rootWindow.ArmoreDeluxeRing && typeof rootWindow.ArmoreDeluxeRing.destroyInRoot === 'function') {
        rootWindow.ArmoreDeluxeRing.destroyInRoot(event.target);
      }
    });
  }
  if (rootWindow && typeof rootWindow.addEventListener === 'function' && rootDocument && rootDocument.readyState !== 'complete') {
    rootWindow.addEventListener('load', () => boot(rootDocument), {once: true});
  }
})();
